import {
  EventConnectionWithId,
  Types,
  Layout,
  PointsEventType,
  SceneModelData,
  SceneCollectedData,
  SceneTimestamps,
  DownloadableFileObject,
  MapInfoCollector,
  MapInfo,
  FileObject,
  deepFindFiles,
  extractFilesFromProp,
  SceneCollectedEvent,
  replaceLayoutAssets,
  SceneCollectedActions,
  SceneCollectedAction,
  SceneVariableActions,
  castIfNumeric,
} from "@citychallenge/common";
import { SceneConnectionEvents } from "./scene-connection";
import { observable, computed, runInAction, action } from "mobx";
import { ScenarioModel } from "../scenario/scenario.model";
import { PointsEvent, POINT_SCALES } from "./points-event";
import { generateRandomId } from "@citychallenge/common";
import { createDownloadTask } from "../../download";
import { DownloadStore } from "../../download";
import { DefaultLogger } from "../../logger";
import { SceneMainMedia } from "./scene-main-media.interface";
import { RedirectV2Props } from "../elements/definitions/redirect-v2";
import { LogicBranchProps } from "../elements";

const collector = new MapInfoCollector();

export class SceneModel<T extends { [key: string]: any } = {}>
  implements SceneModelData<EventConnectionWithId[]>
{
  public id: string;
  public layout: Layout;

  @observable
  public _hotlinkGameName? = "";

  public get element() {
    return this.layout[0];
  }

  @computed
  public get outputPrefix() {
    return this.element.displayName || this.id.replace("sc_", "");
  }

  @computed
  public get isInvisible() {
    if (this.element.name === "logic-branch") {
      const props = this.element.props as LogicBranchProps;

      if (props.type !== "logic") return false;

      const logicOptions = props.logicOptions ? [...props.logicOptions] : [];
      if (props.logicFallback) {
        logicOptions.push(props.logicFallback);
      }

      for (const option of logicOptions) {
        if (option.action === "branch") return false;
      }

      // If none of the options are  branch option, this scene is invisible
      return true;
    }

    return false;
  }

  @computed
  public get completesScenario() {
    // Redirect complete the scenario
    if (
      this.element.name === "redirect-v2" &&
      this.element.props?.["quitStory"]
    ) {
      return true;
    }

    if (
      this.element.name === "leaderboard" &&
      this.scenario?._safeIsLastScene(this)
    ) {
      return true;
    }

    return this.element.name === "redirect";
  }

  @computed
  public get introTextId() {
    return `${this.id}-intro-text`;
  }
  @computed
  public get isBorderless() {
    if (
      this.element.name === "redirect-v2" ||
      this.element.name === "redirect"
    ) {
      if (this.element.props?.["image"]) return false;
      return true;
    }

    if (this.element.name === "leaderboard" && !this.element.props?.["image"]) {
      return true;
    }

    return false;
  }

  @observable
  public name: string;

  public connections: EventConnectionWithId[];
  public bonusWithinSeconds: number;
  public maxPoints: number;
  public variableActions: SceneVariableActions;
  public buttonText?: string;

  public insertedOn = +new Date();

  @observable
  public skipped?: boolean;

  @observable
  public hidden?: boolean;

  @observable
  public explanationText?: Types["MarkdownText"] | null;

  @action
  public setExplanationText = (text: Types["MarkdownText"] | null) => {
    this.explanationText = text;
  };

  @observable
  public timestamps: SceneTimestamps;

  @observable
  public showingPoints = false;

  @observable
  public lockedUntilDate?: Date;

  @action
  public setLockedUntilDate = (date: Date | undefined) => {
    this.lockedUntilDate = date;
  };

  @observable
  public showingExplanation = false;

  /**
   * The offset on which the points component is visible, used by ElementUI to
   * scroll the points component into view
   */
  @observable
  public pointsYOffset = 0;

  @action
  public setPointsYOffset = (offset: number) => {
    this.pointsYOffset = offset;
  };

  public startedBranches: string[];

  /**
   * The offset on which the explanation component is visible, used by ElementUI
   * to scroll the explanation component into view
   */

  @observable
  public explanationYOffset = 0;

  @observable
  public startedByScenes: string[] = [];

  @action
  public setExplanationYOffset = (offset: number) => {
    this.explanationYOffset = offset;
  };

  @observable
  public state: Partial<T>;

  @observable
  public reinitState?: Partial<T>;

  @observable
  public collectedData: SceneCollectedData;

  @observable
  public collectedEvents: SceneCollectedEvent[];

  @action
  public setState = (state: Partial<T>) => {
    this.state = { ...this.state, ...state };
  };

  @computed
  public get outputData() {
    // Add new output system fallbacks to output data, so that it is recalled
    // when display variables of the game are retrieved
    const outputs = this.element.outputs || {};
    const outputFallbacks: { [key: string]: string } = {};

    for (const value of Object.values(outputs)) {
      if (value?.variable && value.fallback)
        outputFallbacks[`@${value.variable}`] = value.fallback;
    }

    const legacyFallbacks: { [key: string]: string } = {};

    for (const [key, value] of Object.entries(
      this.element.outputFallbacks || {}
    )) {
      if (key !== "default" || !this.element.displayName) continue;
      legacyFallbacks[this.element.displayName] = value;
    }

    return {
      ...outputFallbacks,
      ...legacyFallbacks,
      ...this._outputData,
    };
  }

  @observable
  public _outputData: { [key: string]: string };

  @action
  public setOutputData = (data: { [key: string]: string }) => {
    if (this.element.displayName) {
      const save: { [key: string]: string } = {};

      for (const key of Object.keys(data)) {
        if (key !== "default") continue;

        save[this.element.displayName] = data[key];
      }
      this._outputData = { ...this._outputData, ...save };
    }

    const outputs = this.element.outputs || {};

    for (const [key, value] of Object.entries(data)) {
      const variable = outputs[key]?.variable;
      if (variable)
        this.scenario?.game?.setVariables({ [`@${variable}`]: value });
    }
  };

  /*
   * Scene ended is a state where the scene has no governing scenario
   * and therefore marks itself as finished
   */
  @computed
  public get sceneEnded() {
    return !!this.timestamps.finished;
  }

  public scenario?: ScenarioModel;

  @computed
  public get weight() {
    if (!this.scenario) return -1;
    return this.scenario.scenes.findIndex((scene) => scene.id === this.id);
  }

  @computed
  public get maxBranchLength() {
    if (!this.attachedBranches.length) return 0;

    const branches = [...this.attachedBranches].sort(
      (a, b) => b.numScenes - a.numScenes
    );

    return branches.find((s) => !s.started)?.numScenes || 0;
  }

  @observable
  public branched = false;

  @observable
  private _downloadStore: DownloadStore;

  /**
   * All download tasks that this scene needs to have completed
   */
  public get downloadStore() {
    return this._downloadStore;
  }

  public readonly mapInfo: MapInfo[];

  constructor(data: SceneModelData, scenario?: ScenarioModel) {
    this.scenario = scenario;

    this.collectedData = data.collectedData || {};
    this.collectedEvents = data.collectedEvents || [];
    this.collectedActions = data.collectedActions || {};
    this._outputData = data.outputData || {};

    this.lockedUntilDate = data.lockedUntil
      ? new Date(data.lockedUntil)
      : undefined;

    this.id = data.id;
    if (!this.id) throw new Error("Trying to construct scene without id.");

    this.layout = replaceLayoutAssets(data.layout, data.assets || []);
    this.connections = data.connections.map((conn) => {
      if (!conn.id) conn.id = generateRandomId("conn");
      return conn as EventConnectionWithId;
    });
    this.name = data.name;
    this.points = (data.points || []).map((p) => new PointsEvent(p));
    this.state = {};
    this.reinitState = data.reinitState as Partial<T> | undefined;
    if (data.state || data.reinitState)
      Object.assign(this.state, data.state, data.reinitState);
    this.timestamps = data.timestamps || {
      started: 0,
      answered: 0,
      finished: 0,
    };

    this.skipped = data.skipped;
    this.hidden = data.hidden;

    this.branched = data.branched || false;
    this.startedBranches = data.startedBranches || [];
    this.startedByScenes = data.startedByScenes || [];

    this.showingPoints = data.showingPoints || false;
    this.showingExplanation = data.showingExplanation || false;

    this.bonusWithinSeconds = data.bonusWithinSeconds || 0;
    this.explanationText = data.explanationText;
    this.buttonText = data.buttonText;
    this.maxPoints = data.maxPoints || 0;

    this.variableActions = data.variableActions || {};

    this._downloadStore = this.createDownloadStore();

    this.mapInfo = collector.gatherFromScene(this);
  }

  private createDownloadStore() {
    const files = this.getResourceFiles();
    const fileService = this.fileService;

    // Gather all resourceFiles and transform them into download tasks
    // so they can be downloaded before a scene starts
    const tasks = fileService
      ? files.map((file) => createDownloadTask(fileService, file))
      : [];

    return new DownloadStore(tasks);
  }

  @action
  public resetDownloadStore() {
    this._downloadStore = this.createDownloadStore();
  }

  public toJSON(): SceneModelData {
    return {
      id: this.id,
      layout: this.layout,
      connections: this.connections,
      variableActions: this.variableActions,
      name: this.name,
      points: this.points.map((p) => p.toJSON()),
      state: this.state,
      reinitState: this.reinitState,
      timestamps: this.timestamps,
      showingPoints: this.showingPoints,
      showingExplanation: this.showingExplanation,
      collectedData: this.collectedData,
      collectedEvents: this.collectedEvents,
      collectedActions: this.collectedActions,
      outputData: this._outputData,
      bonusWithinSeconds: this.bonusWithinSeconds,
      maxPoints: this.maxPoints,
      branched: this.branched,
      startedBranches: this.startedBranches,
      startedByScenes: this.startedByScenes,
      explanationText: this.explanationText,
      buttonText: this.buttonText,
      skipped: this.skipped,
      hidden: this.hidden,
      lockedUntil: this.lockedUntilDate && this.lockedUntilDate.toString(),
    };
  }

  public onUpdateConnection = (conn: EventConnectionWithId) => {
    const connection = this.connections.find((c) => c.id === conn.id);
    if (!connection) return;
    connection.completed = conn.completed;
  };

  @action
  private _onStart = () => {
    this._setTimestamp("started");
  };

  @action
  private _onAnswer = () => {
    this._setTimestamp("answered");

    // Handle the actions that have an active trigger
    this._handleActions();

    if (this.skipped || !this.maxPoints) return;

    // Calculate points
    this._addPoints(PointsEventType.correct);

    // Calculate timebonus
    if (
      this.bonusWithinSeconds &&
      this.timestamps.answered - this.timestamps.started <
        this.bonusWithinSeconds &&
      this.hintsUsedPenalty >= 0 &&
      this.wrongAnswersPenalty >= 0
    ) {
      this._addPoints(PointsEventType.timeBonus, POINT_SCALES.timeBonus);
    }
  };

  private _handleActions = () => {
    for (const key of Object.keys(this.collectedActions)) {
      const action = this.collectedActions[key];

      runInAction(() => {
        if (action.trigger) {
          this._handleAction(key, action);
        }
      });
    }
  };

  private _handleAction(key: string, { trigger, value }: SceneCollectedAction) {
    if (!trigger) return;

    const currentParams = this.scenario?.game?.variables || {};
    const variableActions = this.variableActions[key] || [];
    const params: { [key: string]: string } = {};

    for (const action of variableActions) {
      const variableId = `@${action.variableId}`;

      if (value !== undefined) {
        if (action.action === "assign-add") {
          params[variableId] = `${
            castIfNumeric(currentParams[variableId], 0) +
            castIfNumeric(value, 0)
          }`;
        } else {
          params[variableId] = value || action.fallback || "";
        }
        continue;
      }

      if (action.action === "set") {
        params[variableId] = `${action.value}`;
      }

      if (action.action === "add") {
        params[variableId] = `${
          castIfNumeric(currentParams[variableId], 0) +
          castIfNumeric(action.value, 0)
        }`;
      }

      if (action.action === "subtract") {
        params[variableId] = `${
          castIfNumeric(currentParams[variableId], 0) -
          castIfNumeric(action.value, 0)
        }`;
      }
    }

    this.scenario?.game?.setVariables(params);
  }

  @computed
  public get attachedBranches() {
    if (!this.connections?.length) return [];

    const branches = this.scenario?.game?.branches;

    if (!branches?.length) return [];

    return this.connections
      .filter((value) => value.to.prop === "startBranch")
      .map((value) => {
        return branches.find((scenario) => value.to.value === scenario.id);
      })
      .filter((v) => !!v) as ScenarioModel[];
  }

  public resetScenarioToSelf() {
    // Scenario was branched before, so we will remove those branches
    this.scenario?.filterScenes((scene) => {
      return !scene.startedByScenes?.includes(this.id);
    });

    this.scenario?.resetScenarioTo(this.id);
  }

  /**
   * Return the main media of the scene, if any. This is used to preload images
   * and videos before the scene starts. Especially useful for branched scenes.
   */
  @computed
  public get mainMedia(): SceneMainMedia | undefined {
    if (this.element.props?.["headerImage"]) {
      return {
        preset: "header",
        file: this.element.props["headerImage"],
      };
    }

    if (this.element.props?.["video"]) {
      return {
        preset: "video",
        file: this.element.props["video"],
      };
    }

    if (
      this.element.name === "true-or-false" &&
      this.element.props?.["image"]
    ) {
      return {
        preset: "header",
        file: this.element.props["image"],
      };
    }

    if (
      this.element.name === "image-hotspots" &&
      this.element.props?.["image"]
    ) {
      return {
        preset: "image-hotspots",
        file: this.element.props["image"],
      };
    }

    if (this.element.props["media"]) {
      return {
        preset: "header",
        file: this.element.props["media"],
      };
    }

    if (this.element.props["trackable"]?.source) {
      return {
        preset: "header",
        file: this.element.props["trackable"]?.source,
        disableBranchPreload: true,
      };
    }

    if (this.element.props["renderable"]?.source) {
      return {
        preset: "header",
        file: this.element.props["renderable"]?.source,
        disableBranchPreload: true,
      };
    }

    if (this.element.name === "redirect") {
      const image =
        this.scenario?.game?.storyIcon || this.scenario?.game?.image;

      if (!image) return undefined;
      return {
        preset: "header",
        file: {
          path: image,
          pathType: "remote",
        },
      };
    }

    if (this.element.name === "redirect-v2") {
      const fallback =
        this.scenario?.game?.storyIcon || this.scenario?.game?.image;
      const props: RedirectV2Props = this.element.props;

      for (const item of props.options || []) {
        const image = item.image?.path || item.story?.image?.path;
        if (image)
          return {
            preset: "header",
            file: {
              path: image,
              pathType: "remote",
            },
          };
      }
      if (fallback)
        return {
          preset: "header",
          file: {
            path: fallback,
            pathType: "remote",
          },
        };
    }

    return;
  }

  @action
  public __dangerouslyHideSelfFromScenario = () => {
    this.hidden = true;
  };

  /**
   * Use this function only to start branches from the outside of normal
   * 'onAnswer' and event flow. This is a dangerous function and should only
   * be used in specific cases.
   * @param branchId
   */
  @action
  public __dangerouslyInsertBranch = (
    branchId: string | null,
    replace = false
  ) => {
    this._setTimestamp("started");

    if (replace) {
      this.__dangerouslyHideSelfFromScenario();
    }

    if (branchId === null) {
      return;
    }

    const branch = this.scenario?.game?.branches?.find(
      (branch) => branch.id === branchId
    );

    this.branched = true;

    if (!branch) {
      DefaultLogger.captureObject("Could not insert branch", {
        branchId: branch,
        gameId: this.scenario?.game?.id,
        sceneId: this.id,
      });

      return { scenes: undefined, branch: undefined };
    }

    branch.setStarted(true);

    const scenes = branch.scenes.map((scene) =>
      SceneModel.resetModel(scene, this.scenario, () => ({
        id: `${scene.id}_${this.scenario?.scenes.length}`,
        startedByScenes: [...this.startedByScenes, this.id],
      }))
    );
    this.scenario?.insertScenes(scenes, 0, this.id);
    return { scenes, branch };
  };

  public __dangerouslyStartBranch = (branchId: string) => {
    this._startBranch(branchId);
  };

  public __dangerouslyInsertLoopsAndJumps(branch: ScenarioModel) {
    const availableBranches = this.attachedBranches.filter(
      (b) =>
        b.scenes.length &&
        !this.startedBranches.includes(b.id) &&
        b.id !== branch.id
    );

    if (branch.mode === "loopback" && availableBranches.length) {
      // If the branch want to loop back to the current scene, and there are still
      // available branches with content, we simply add a duplicate of the current
      // scene on top of the scene stack.
      this.scenario?.insertScenes(
        [
          SceneModel.resetModel(this, this.scenario, (sceneData) => ({
            id: `${this.id}_${this.scenario?.scenes.length}`,
            startedBranches: [...this.startedBranches, branch.id],
            startedByScenes: [...this.startedByScenes, this.id],
            reinitState: {
              ...sceneData.state,
              activeOption: undefined,
            },
          })),
        ],
        branch.scenes.length
      );
    }

    if (branch.mode === "jump" && branch.jumpTarget) {
      const target =
        this.scenario?.findNearestSceneStartingWith(branch.jumpTarget) ||
        this.scenario?.game?.findSceneById(branch.jumpTarget);

      this.scenario?.filterScenes((scene) => {
        // When jumping, remove immediate branch siblings that start after this scene.
        // This prevent unwanted scenes of sticking around after the jump
        if (
          !scene.isStarted &&
          scene.startedByScenes.length > 0 &&
          JSON.stringify(scene.startedByScenes) ===
            JSON.stringify(this.startedByScenes)
        ) {
          return false;
        }
        return true;
      });

      if (target) {
        this.scenario?.insertScenes(
          [
            SceneModel.resetModel(target, target.scenario, (sceneData) => ({
              id: `${target.id}_${target.scenario?.scenes.length}`,
              startedBranches: [...this.startedBranches, branch.id],
              startedByScenes: [...this.startedByScenes, this.id],
              reinitState: {
                ...sceneData.state,
                activeOption: undefined,
              },
            })),
          ],
          branch.scenes.length
        );
      }
    }
  }

  @action
  private _startBranch = (branchId: string) => {
    this.setState({
      ...this.state,
      branchEventFired: true,
    });

    // First find branch inside game info
    const branch = this.scenario?.game?.branches?.find(
      (branch) => branch.id === branchId
    );

    if (!branch) {
      DefaultLogger.captureObject("Could not start branch", {
        branchId: branch,
        gameId: this.scenario?.game?.id,
        sceneId: this.id,
      });

      this._onContinue(true);
      return;
    }

    this.collectEvent({ event: "start-branch", value: branchId });

    if (this.skipped || this.branched || this.isFinished) {
      this.resetScenarioToSelf();
    }

    this.branched = true;

    branch.setStarted(true);

    this.scenario?.insertScenes(
      branch.scenes.map((scene) =>
        SceneModel.resetModel(scene, this.scenario, () => ({
          id: `${scene.id}_${this.scenario?.scenes.length}`,
          startedByScenes: [...this.startedByScenes, this.id],
        }))
      )
    );

    this.__dangerouslyInsertLoopsAndJumps(branch);

    this._onContinue(true);
  };

  @action
  private _onContinue = (force = false) => {
    if (this.isFinished && !force) return;

    if (!this.isAnswered) this._onAnswer();

    if (this.totalPoints && !this.showingPoints) {
      this.showingPoints = true;
      if (!this.skipped) return;
    }

    const hasExplanation = this.explanationText && this.explanationText.text;
    if (hasExplanation && !this.showingExplanation) {
      this.showingExplanation = true;
      if (!this.skipped) return;
    }

    this._setTimestamp("finished");
    if (this.scenario) {
      this.scenario.onSceneFinish(this);
    }
  };

  public readonly lifecycle = {
    onStart: () => this._onStart(),
    onAnswer: () => this._onAnswer(),
    continue: () => this._onContinue(),
    forceContinue: () => this._onContinue(true),
  };

  /**
   * The scene length (this scene plus longest branch)
   */
  @computed
  public get sceneLength() {
    return this.branched || this.skipped || this.isFinished
      ? 1
      : 1 + this.maxBranchLength;
  }

  @action
  public skip = () => {
    if (!this.skipped && !this.isAnswered) {
      // Run listeners, and if one returns true, assume that the skip has
      // been handled somewhere else, or blocked
      if (this.runListeners("skip")) return;

      this.points.splice(0, this.points.length);
      this.skipped = true;
    }

    this._onContinue();
  };

  public readonly events: SceneConnectionEvents = {
    startBranch: (branchId: string) => this._startBranch(branchId),
    onFinish: () => this._onContinue(),
  };

  @action
  public collect = (data: SceneCollectedData) => {
    this.collectedData = { ...this.collectedData, ...data };
  };

  @action
  public collectEvent = (event: Omit<SceneCollectedEvent, "timestamp">) => {
    const existing = this.collectedEvents || [];
    this.collectedEvents = [
      ...existing,
      { ...event, timestamp: Math.floor(Date.now() / 1000) },
    ];
  };

  /**
   * Collected actions store which actions must be triggered on answer or skip
   * of the scene. Actions can only be executed once.
   */
  @observable
  public collectedActions: SceneCollectedActions = {};

  @action
  public collectAction = (action: string, trigger: boolean, value?: string) => {
    this.collectedActions[action] = { trigger, value };
  };

  @computed
  public get isActive() {
    if (!this.scenario) return true;
    return this.scenario.activeSceneId === this.id;
  }

  @computed
  public get isStarted() {
    return !!this.timestamps.started;
  }

  @computed
  public get isAnswered() {
    return !!this.timestamps.answered;
  }

  @computed public get isCompleted() {
    return this.isAnswered;
  }

  @computed
  public get isFinished() {
    return !!this.timestamps.finished;
  }

  private _setTimestamp(timestamp: keyof SceneTimestamps) {
    if (this.timestamps[timestamp]) return;
    this.timestamps[timestamp] = Math.floor(Date.now() / 1000);
  }

  // Points and hint system
  @observable
  public readonly points: PointsEvent[] = [];

  /**
   * Unlock a hunt and add it to the point events
   */
  @action
  public unlockHint = () => {
    this._addPoints(PointsEventType.hint, POINT_SCALES.hint);
  };

  /**
   * Add a wrong answer to the point events
   */
  @action
  public addWrongAnswer = (scale = POINT_SCALES.wrong) => {
    this._addPoints(PointsEventType.wrong, scale);
  };

  /**
   * Add a points with given scales.
   *
   * Number of entries in the points array
   * can never exceed the length of the scales arrayt.
   */
  private _addPoints(type: PointsEventType, scales = [1]) {
    const current = this._numEntriesOfPointsType(type);
    const scaleValue = scales[current] || 0;

    this.points.push(
      new PointsEvent({
        points: scaleValue * this.maxPoints,
        type,
      })
    );
  }

  private _numEntriesOfPointsType(type: PointsEventType) {
    return this.points.filter((p) => p.type == type).length;
  }

  /**
   * Number of hints used
   */
  @computed public get hintsUsed() {
    return this._numEntriesOfPointsType(PointsEventType.hint);
  }

  /**
   * Number of wrong ansers given
   */
  @computed public get wrongAnswers() {
    return this._numEntriesOfPointsType(PointsEventType.wrong);
  }

  /**
   * Sum points of a given type to a total (omit type to sum all points)
   */
  private _sumPoints(type?: PointsEventType) {
    let points = this.points;
    if (type) {
      points = points.filter((p) => p.type == type);
    }
    return points.reduce<number>((prevP, points) => prevP + points.points, 0);
  }

  /**
   * Time bonus received
   */
  @computed public get correctAnswerPoints() {
    return this._sumPoints(PointsEventType.correct);
  }

  /**
   * Total penalty on points of all hints used
   */
  @computed public get hintsUsedPenalty() {
    return this._sumPoints(PointsEventType.hint);
  }

  /**
   * Total penalty of points of all wrong answers given
   */
  @computed public get wrongAnswersPenalty() {
    return this._sumPoints(PointsEventType.wrong);
  }

  /**
   * Time bonus received
   */
  @computed public get timeBonusPoints() {
    return this._sumPoints(PointsEventType.timeBonus);
  }

  /**
   * Total of all points scored by the user
   */
  @computed public get totalPoints() {
    if (!this.maxPoints) return 0;
    return this._sumPoints();
  }

  /**
   * Wether or not this scene is running the hotlink/developer mode feature
   * of the platform
   */
  @computed
  public get isRunningInHotlink() {
    return !this.scenario || this.scenario.isRunningInHotlink;
  }

  /**
   * Check if element is locked by `lockedUntil`
   *
   * Note: Not computed because it uses current date as calculation.
   */
  public get isTimeLocked() {
    if (!this.lockedUntilDate) return false;

    return this.lockedUntilDate > new Date();
  }

  public get fileCollector() {
    return this.scenario?.game?.fileCollector;
  }

  public get fileService() {
    return this.scenario?.game?.fileService;
  }

  /**
   * All files for this scvene that are of type 'resource' and have their
   * originalPath defined
   */
  public getResourceFiles(): DownloadableFileObject[] {
    const files =
      this.fileCollector?.gatherSceneFiles(this).filter((object) => {
        return object.pathType === "resource" && object.originalFilePath;
      }) ?? [];

    return files as DownloadableFileObject[];
  }

  public getFiles(): FileObject[] {
    return [
      ...deepFindFiles(this.layout),
      ...extractFilesFromProp(this.explanationText),
    ];
  }

  @computed
  public get failedDownloadTasks() {
    return this.downloadStore.failedDownloadTasks;
  }

  private _listeners: { event: string; fn: () => boolean }[] = [];

  public on = (event: "skip", fn: () => boolean) => {
    // Attach event listener
    this._listeners.push({ event, fn });

    return () => {
      this._listeners = this._listeners.filter(
        (obj) => obj.event !== event && obj.fn !== fn
      );
    };
  };

  private runListeners = (event: "skip") => {
    let hasHandled = false;

    for (const listener of this._listeners) {
      if (listener.event !== event) continue;

      const result = listener.fn();

      if (result) hasHandled = true;
    }

    return hasHandled;
  };

  public static resetModel(
    model: SceneModel,
    scenario?: ScenarioModel,
    overrides?: (sceneData: SceneModelData) => Partial<SceneModelData>
  ) {
    const sceneData = model.toJSON();
    return new SceneModel(
      {
        ...sceneData,
        timestamps: undefined,
        showingExplanation: undefined,
        showingPoints: undefined,
        connections: sceneData.connections.map((connection) => ({
          ...connection,
          completed:
            model.element.name === "map" ? connection.completed : false,
        })),
        state: undefined,
        collectedEvents: [],
        collectedData: undefined,
        collectedActions: {},
        points: [],
        skipped: false,
        branched: false,
        hidden: false,
        ...overrides?.(sceneData),
      },
      scenario
    );
  }
}
